import { accountDocumentUrl } from "@utils/api";
import { PermissionGate } from "@components/PermissionGate";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Button,
  Group,
  LoadingOverlay,
  Menu,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDownload,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash
} from "@tabler/icons-react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

import { openDeleteModal } from "@components/DeleteModal";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { QP } from "@constants/index";

import { useDeleteClinicFile } from "@hooks/mutations/clinic/useDeleteClinicFile";
import { useGetClinicFiles } from "@hooks/queries/clinic/useGetClinicFiles";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { useAuth } from "@contexts/auth";

import { ClinicFile } from "@customTypes/clinic";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

import { AddFileModal } from "./components/AddFileModal";
import { EditFileModal } from "./components/EditFileModal";


export const FilesTab = () => {
  const { t, i18n } = useTranslation();

  // Table column count for skeleton loading states
  const { tableRef, columnCount } = useTableColumnCount();
  const { token } = useAuth();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination, setPage } =
    useSyncTableQueryParams({ searchFilter });



  const [filterTag, setFilterTag] = useState<string>("all");
  const [doctorId, setDoctorId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<ClinicFile | null>(null);

  const [
    addFileModalOpened,
    { open: openAddFileModal, close: closeAddFileModal },
  ] = useDisclosure(false);

  const [
    editFileModalOpened,
    { open: openEditFileModal, close: closeEditFileModal },
  ] = useDisclosure(false);

  const { data, isLoading, isRefetching, isError, isSuccess, fetchStatus } = useGetClinicFiles({
    search: debounced.trim(),
    doctorId: doctorId || undefined,
  });
  const {
    mutate: deleteFile,
    isLoading: isDeleteFileLoading,
  } = useDeleteClinicFile();

  // Get all users to map user IDs to names
  const { data: usersData, isLoading: isUsersLoading } = useGetUsers({ accountOptions: true });
  const { data: doctorsData } = useGetUsers({ role: "doctor", accountOptions: true });

  const resetPage = () => {
    setTablePagination(previous => ({ ...previous, page: 1 }));
    setPage(1);
  };

  // Helper function to get user name by ID
  const getUserName = (userId: string) => {
    if (!userId) return "N/A";
    if (isUsersLoading) return t("loading");
    if (!usersData) return "N/A";
    const user = usersData.find(user => user.id === userId);
    return user?.fullName || "Unknown User";
  };

  const handleDownload = async (file: ClinicFile) => {
    if (file.url) {
      try {
        // Construct the proper URL with https:// prefix and token
        const downloadUrl = accountDocumentUrl(file.url, token);

        // Fetch the file as a blob to force download
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the blob data
        const blob = await response.blob();

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.fileName || 'download';

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback to opening in new tab if fetch fails
        const downloadUrl = accountDocumentUrl(file.url, token);
        window.open(downloadUrl, '_blank');
      }
    } else {
      console.log('Download file:', file.fileName);
      // TODO: Implement actual download logic
    }
  };

  const handleEdit = (file: ClinicFile) => {
    setSelectedFile(file);
    openEditFileModal();
  };



  // Filter data based on selected tag
  const filteredData = useMemo(() => {
    if (!data?.newData) return [];

    if (filterTag === "all") {
      return data.newData;
    }

    return data.newData.filter(file => file.tag === filterTag);
  }, [data?.newData, filterTag]);
  const pageSize = tablePagination.pageSize || 25;
  const pageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPage = Math.min(tablePagination.page || 1, pageCount);
  const visibleData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Files table rows (AdminDashboard pattern)
  const fileRows = useMemo(() => {
    return visibleData.map(file => (
      <Table.Tr key={file.id}>
        <Table.Td>
          {(() => {
            const value = file.fileName || "-";
            const isLong = value && value.length > 30;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={500}
                  lh={1.5}
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {file.tag || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {getUserName(file.assignedDoctor)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Menu shadow="md" width={150} position="bottom-end">
              <Menu.Target>
                <Button
                  variant="outline"
                  size="compact-sm">
                  {t("action")}
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconDownload size={16} />}
                  onClick={() => handleDownload(file)}>
                  {t("download")}
                </Menu.Item>
                <PermissionGate permission="clinic_files.edit"><Menu.Item
                  leftSection={<IconPencil size={16} />}
                  onClick={() => handleEdit(file)}>
                  {t("edit")}
                </Menu.Item></PermissionGate>
                <PermissionGate permission="clinic_files.delete"><Menu.Item
                  leftSection={<IconTrash size={16} />}
                  color="gray"
                  disabled={true}
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  onClick={() => {
                    openDeleteModal({
                      title: t("delete-file"),
                      textNode: (
                        <Text size="sm" lh={1.5}>
                          <Trans
                            i18nKey="delete-file-confirmation"
                            values={{ fileName: file.fileName }}
                            components={{ bold: <Text span fw={700} /> }}
                          />
                        </Text>
                      ),
                      confirmText: t("delete-file"),
                      onConfirm: () => {
                        deleteFile(file.id);
                      },
                    });
                  }}>
                  {t("delete")}
                </Menu.Item></PermissionGate>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [visibleData, i18n.language, getUserName]);



  return (
    <StyledLaboratory>
      <StyledResourceManagement>
        <LoadingOverlay
          visible={isRefetching && !!data?.newData?.length}
          zIndex={1000}
          overlayProps={{ blur: 0.1, opacity: 0.6 }}
          loaderProps={{ display: "none" }}
        />

        <div className="header">
          <Group w={"100%"} justify="space-between" align="center">
            <h1>Files</h1>
            <PermissionGate permission="clinic_files.create"><Button
              leftSection={<IconPlus size={16} />}
              onClick={openAddFileModal}>
              {t("add-file")}
            </Button></PermissionGate>
          </Group>
        </div>

        <Group w={"100%"} mt={"md"} gap={16} align="center">
          <TextInput
            placeholder={t("search-for-a-file")}
            leftSection={<IconSearch size={16} />}
            value={searchFilter || ""}
            onChange={event => setSearchFilter(event.currentTarget.value || null)}
            style={{ flex: 1, maxWidth: 400 }}
          />
            <Select
              placeholder={t("all-files")}
              data={[
                { value: "all", label: t("all-files") },
                { value: "logo", label: t("logo") },
                { value: "signature", label: t("signature") },
                { value: "license", label: t("license") },
                { value: "other", label: t("other") },
              ]}
              value={filterTag}
              onChange={(value) => { setFilterTag(value || "all"); resetPage(); }}
              style={{ minWidth: 120 }}
            />
          <Select
            placeholder={t("all-doctors")}
            searchable
            clearable
            data={(doctorsData || []).map(doctor => ({
              value: doctor.id,
              label: doctor.fullName || doctor.username,
            }))}
            value={doctorId}
            onChange={value => { setDoctorId(value); resetPage(); }}
            style={{ minWidth: 200 }}
          />
        </Group>

        <div style={{paddingBottom: "200px"}}>
          <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
            <Table
              ref={tableRef}
              withRowBorders={true}
              mt={"1rem"}
              miw={1280}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
              <Table.Thead>
                <Table.Tr bg={"var(--clr-gray-100)"}>
                  <Table.Th>{t("file-name")}</Table.Th>
                  <Table.Th>{t("tag")}</Table.Th>
                  <Table.Th>{t("assigned-doctor")}</Table.Th>
                  <Table.Th>{t("action")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {renderLoadingErrorData({
                  isLoading: isLoading && fetchStatus === "fetching",
                  isError,
                  isSuccess,
                  dataLength: filteredData?.length || 0,
                  errorComponent: (
                    <TableErrorRow
                      colSpan={columnCount}
                      message={t("encountered-error")}
                    />
                  ),
                  loadingComponent: (
                    <TableSkeletonRows
                      columns={columnCount}
                      skeletonProps={{
                        widths: ["lg", "md", "md", "md"],
                      }}
                    />
                  ),
                  emptyDataComponent: (
                    <TableEmptyRow
                      colSpan={columnCount}
                      message={
                        debounced || filterTag !== "all" || doctorId
                          ? (debounced ? t("no-files-found") : t("no-files-for-selected-type"))
                          : t("no-files-available")
                      }
                    />
                  ),
                  dataComponent: fileRows,
                })}
              </Table.Tbody>
            </Table>

            <TablePagination
              paginationData={{ page: currentPage, pageCount, pageSize, total: filteredData.length }}
              paginatedRows={visibleData.length}
              onPageChange={(page) => { setTablePagination(prev => ({ ...prev, page })); setPage(page); }}
              isLoading={isLoading}
            />
          </ScrollArea>
        </div>

        <PermissionGate permission="clinic_files.create"><AddFileModal
          opened={addFileModalOpened}
          close={closeAddFileModal}
        /></PermissionGate>

        <PermissionGate permission="clinic_files.edit"><EditFileModal
          opened={editFileModalOpened}
          close={closeEditFileModal}
          file={selectedFile}
        /></PermissionGate>
      </StyledResourceManagement>
    </StyledLaboratory>
  );
};
